import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Search, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessagesPage = ({ user }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const res = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
    } catch (e) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const token = localStorage.getItem('kaplie_token');
      const res = await axios.get(`${API}/users/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data);
    } catch (e) {}
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="fixed inset-0 bg-gradient-to-br from-[#D4AF37]/[0.04] via-black to-violet-900/[0.06] pointer-events-none" />

      <nav className="relative z-10 border-b border-white/10 bg-black/50 backdrop-blur-xl sticky top-0">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-white/60" />
              </button>
            </Link>
            <MessageCircle className="w-5 h-5 text-[#D4AF37]" />
            <h1 className="text-lg font-semibold">Messages</h1>
          </div>
          <button
            onClick={() => setShowSearch(!showSearch)}
            data-testid="new-message-btn"
            className="p-2 bg-[#D4AF37]/15 hover:bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black/30 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5 text-[#D4AF37]" />
          </button>
        </div>
      </nav>

      <main className="relative z-10 max-w-3xl mx-auto px-6 py-4">
        {/* Search for new conversation */}
        {showSearch && (
          <div className="mb-4">
            <div className="relative mb-3">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => searchUsers(e.target.value)}
                placeholder="Search by name or email..."
                autoFocus
                data-testid="user-search-input"
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/50"
              />
            </div>
            {searchResults.length > 0 && (
              <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden mb-4">
                {searchResults.map((u) => (
                  <Link to={`/chat/${u.id}`} key={u.id}>
                    <div className="p-3 hover:bg-white/10 transition-colors flex items-center gap-3 border-b border-white/5 last:border-0">
                      <div className="w-9 h-9 bg-gradient-to-br from-[#D4AF37]/20 to-[#C5A059]/10 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold">{(u.name || '?').charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{u.name}</p>
                        <p className="text-xs text-white/40">{u.email}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Conversations List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-white/20 border-t-[#D4AF37] rounded-full animate-spin" />
          </div>
        ) : conversations.length === 0 && !showSearch ? (
          <div className="text-center py-20">
            <MessageCircle className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/40 mb-2">No messages yet</p>
            <button onClick={() => setShowSearch(true)} className="text-[#D4AF37] text-sm hover:text-[#D4AF37]/80">
              Start a conversation
            </button>
          </div>
        ) : (
          <div data-testid="conversations-list">
            {conversations.map((conv) => (
              <Link to={`/chat/${conv.other_user_id}`} key={conv.id}>
                <div className="p-4 hover:bg-white/5 rounded-xl transition-colors flex items-center gap-3 mb-1">
                  <div className="relative">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37]/20 to-[#C5A059]/10 rounded-full flex items-center justify-center">
                      <span className="text-lg font-bold">{(conv.other_user_name || '?').charAt(0).toUpperCase()}</span>
                    </div>
                    {conv.unread > 0 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black rounded-full flex items-center justify-center">
                        <span className="text-[10px] font-bold">{conv.unread}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-0.5">
                      <p className={`font-medium text-sm ${conv.unread > 0 ? 'text-white' : 'text-white/80'}`}>
                        {conv.other_user_name}
                      </p>
                      <span className="text-xs text-white/30">{formatTime(conv.last_message_at)}</span>
                    </div>
                    <p className={`text-sm truncate ${conv.unread > 0 ? 'text-white/70 font-medium' : 'text-white/40'}`}>
                      {conv.last_sender_name === user?.name ? 'You: ' : ''}{conv.last_message}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MessagesPage;
